/**
 * Showing a file the user attached, on a device that never had the file.
 *
 * The browser can only display an attachment it holds the bytes of, and it
 * only holds them for the moments after the user picks one. Everything else —
 * a reload, another device, the reconnect that re-reads a chat — has nothing
 * but what the agent recorded, which is a reference:
 *
 *   media://inbound/816cf3a3-927f-47f5-889c-a5ba900bffdd.jpg
 *
 * That is openclaw's own scheme, not a URL a browser can load, and openclaw
 * publishes an HTTP route for outgoing media only. So the terminal's sidecar
 * serves that directory (`/sync/v1/media/inbound/<id>`) and this fetches from
 * it — authenticated, which is why it cannot simply be an `<img src>`: a
 * browser sends no headers on an image load. The bytes become an object URL,
 * and the picture renders wherever the chat is opened.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { MessageAttachment } from '@paradigm/terminal-core';

import type { SidecarRequest } from '../../../sidecar/request';

/** The scheme openclaw records an inbound attachment under. */
const MEDIA_REF_PREFIX = 'media://inbound/';

/**
 * The address openclaw publishes a file the AGENT SENT at.
 *
 * Recognised, and then deliberately NOT used. That address is gated by a
 * five-minute ticket: fine for showing a picture the instant it is sent,
 * useless for a transcript, which is read again tomorrow. So a message
 * carrying it is served the copy on the pod's disk instead, and the key is
 * the file's own NAME — the id in this address appears nowhere on disk, and
 * only openclaw's own database ties the two together.
 */
const OUTGOING_MEDIA_PREFIX = '/api/chat/media/outgoing/';

/** A plain file name: what the outbound store can be asked for. */
const PLAIN_FILE_NAME = /^[A-Za-z0-9][A-Za-z0-9 ._-]{0,127}$/u;

/**
 * Where the agent's own working directory begins, inside an absolute path.
 *
 * A frame names a file it produced as a pod path
 * (`/home/node/.openclaw/workspace/tmp/circle-100x100.png`) and the sidecar
 * serves that tree by the path RELATIVE to its root. This used to match the
 * default root as a literal prefix, but the sidecar reads its root from
 * `OPENCLAW_WORKSPACE_DIR` — so a pod configured with any other one produced
 * paths this refused silently: a grey chip with nothing to explain it.
 *
 * The segment is the signal instead. Whatever follows the last `/workspace/`
 * is the workspace-relative path, which holds for every root ending in
 * `workspace`; a path with no such segment is not one of these and is not
 * fetched.
 */
const WORKSPACE_SEGMENT = '/workspace/';

/**
 * Object URLs already made, keyed by the route that produced them.
 *
 * One fetch per file rather than one per render: the same attachment is
 * re-rendered on every scroll and every chat switch, and an object URL pins
 * its bytes in memory until it is revoked — so the cache has to be bounded,
 * and the bound has to know four things the first version of it did not.
 *
 * IN USE. Revoking a URL a mounted `<img>` still points at leaves a broken
 * image, and the transcript is not virtualized — every message renders — so a
 * chat with more than the cap of pictures mounts them all at once and image
 * 41 used to revoke image 1 while image 1 was on screen. Worse after lazy
 * fetch: the hook's state still held the revoked URL and none of its
 * dependencies had changed, so nothing refetched and the glyph stayed until
 * the component unmounted. Entries with a live consumer are never evicted.
 *
 * BYTES, not just count. The sidecar caps a file at 32 MB, so forty entries
 * was a 1.2 GB worst case held for the life of the tab.
 *
 * WHOSE. The key is a route, and a route carries no account, so after an
 * account switch one user's attachment bytes stayed in memory and a workspace
 * path could collide with the same path under the other account. Cleared on
 * an account change (`clearAttachmentPreviews`).
 *
 * HOW LONG. An attachment id names an immutable file and can be held for the
 * tab's life; a workspace path is a name the agent can WRITE AGAIN — the
 * sidecar says so with a 60-second `Cache-Control` — so caching one forever
 * showed a regenerated chart as the previous image until the tab was closed.
 * A mutable route gets the same 60 seconds here.
 */
const MAX_CACHED_PREVIEWS = 40;
const MAX_CACHED_BYTES = 64 * 1024 * 1024;
const MUTABLE_PREVIEW_TTL_MS = 60_000;
/** The one route whose answer can change under a fixed name. */
const MUTABLE_ROUTE_PREFIX = '/sync/v1/media/workspace/';

interface CachedPreview {
  readonly url: string;
  readonly bytes: number;
  /** When this stops being trustworthy; `Infinity` for an immutable file. */
  readonly expiresAt: number;
  /** Mounted components currently rendering this URL. */
  consumers: number;
}

const cache = new Map<string, CachedPreview>();

/** One fetch, however many bubbles are showing the file it will produce. */
interface InFlightPreview {
  readonly promise: Promise<string | null>;
  readonly controller: AbortController;
  /**
   * Mounted components awaiting this fetch.
   *
   * Counted because the abort belongs to the fetch, not to whichever bubble
   * happened to start it: cancelling when THAT one unmounts would pull the
   * transfer out from under every other bubble still waiting on the same
   * promise. So the transfer stops when the count reaches zero and not before,
   * which is what makes scrolling past a chat full of pictures stop the
   * requests it left behind — each up to the sidecar's 32 MB cap.
   */
  waiters: number;
}
const inFlight = new Map<string, InFlightPreview>();

/**
 * Bumped whenever the cache is cleared, which is on an account change.
 *
 * A fetch already in the air when that happens cannot be allowed to file its
 * bytes: clearing removes the map ENTRY, but the promise it removed keeps
 * running and would put the departed account's file back in the cache a moment
 * later, under a fresh lifetime and with nothing rendering it. For a file the
 * user attached that is merely wasteful — the key carries a per-file id, so
 * nothing will ever ask for it again — but a picture the agent produced is
 * addressed by its path in the agent's working directory, which is the same
 * path under either account. That is the collision this cache is cleared to
 * prevent, so one account's chart could be served to the next from cache.
 *
 * A fetch compares this against the value it started with and throws its bytes
 * away rather than filing them.
 */
let cacheEpoch = 0;

function cachedPreview(route: string): string | null {
  const entry = cache.get(route);
  if (entry == null) return null;
  if (entry.expiresAt <= Date.now() && entry.consumers === 0) {
    cache.delete(route);
    URL.revokeObjectURL(entry.url);
    return null;
  }
  return entry.url;
}

function cachedBytes(): number {
  let total = 0;
  for (const entry of cache.values()) total += entry.bytes;
  return total;
}

function remember(
  route: string,
  url: string,
  bytes: number,
  ttl: number,
): void {
  cache.set(route, {
    url,
    bytes,
    expiresAt: ttl === Infinity ? Infinity : Date.now() + ttl,
    consumers: 0,
  });
  // Oldest first, and only what nothing is rendering. A cache over its bound
  // whose every entry is on screen stays over its bound — which is the right
  // answer: the alternative is a wall of broken images.
  for (const [key, entry] of cache) {
    if (
      cache.size <= MAX_CACHED_PREVIEWS &&
      cachedBytes() <= MAX_CACHED_BYTES
    ) {
      break;
    }
    if (key === route || entry.consumers > 0) continue;
    cache.delete(key);
    URL.revokeObjectURL(entry.url);
  }
}

/** Register/release a mounted consumer, so eviction can leave it alone. */
function retainPreview(route: string): void {
  const entry = cache.get(route);
  if (entry != null) entry.consumers += 1;
}

function releasePreview(route: string): void {
  const entry = cache.get(route);
  if (entry != null && entry.consumers > 0) entry.consumers -= 1;
}

/**
 * Drop every cached preview and release its bytes.
 *
 * Called when the account changes: these are one user's files, and the key
 * says nothing about whose they are.
 */
export function clearAttachmentPreviews(): void {
  cacheEpoch += 1;
  for (const entry of cache.values()) URL.revokeObjectURL(entry.url);
  cache.clear();
  // Stopped, not just forgotten. Whatever is still downloading belongs to the
  // account being left and nothing is going to render it, and forgetting it
  // alone left it running to completion and then filing itself. The epoch
  // above is what makes that safe even for a transfer already past the point
  // an abort can reach.
  for (const entry of inFlight.values()) entry.controller.abort();
  inFlight.clear();
}

/**
 * The attachment id in openclaw's reference, or null when this is not one.
 *
 * Deliberately strict: the id is one path segment of the shape openclaw
 * mints, so a reference carrying anything else — a nested path, a query, an
 * absolute URL — is not something to build a request from. The sidecar
 * refuses those too; this simply never asks.
 */
export function inboundMediaId(ref: string | undefined): string | null {
  if (ref == null || !ref.startsWith(MEDIA_REF_PREFIX)) return null;
  const id = ref.slice(MEDIA_REF_PREFIX.length);
  return /^[0-9a-f-]{36}(\.[A-Za-z0-9]{1,8})?$/u.test(id) ? id : null;
}

/**
 * The sidecar path that serves this attachment, or null when nothing can.
 *
 * TWO STORES, one door each. What the user attached lives in openclaw's media
 * directory, addressed by the id the transcript records. What the agent
 * produced lives in its workspace, addressed by a path — openclaw does publish
 * a route for that one, but it is ticket-gated and addressed by an artifact id,
 * and the file is on the same disk the sidecar already reads.
 *
 * Anything else — an unrecognised scheme, a path outside the workspace — is
 * not fetched at all. The sidecar would refuse it; this does not ask.
 */
export function mediaRouteFor(attachment: MessageAttachment): string | null {
  const ref = attachment.sourceUrl;
  if (ref == null || ref.length === 0) return null;

  const inbound = inboundMediaId(ref);
  if (inbound != null) return `/sync/v1/media/inbound/${inbound}`;

  // A file the agent sent: served from openclaw's copy, by name. The name is
  // the only thing both the message and the disk agree on — and without one
  // there is nothing to ask for, so the bubble keeps its chip.
  if (ref.startsWith(OUTGOING_MEDIA_PREFIX)) {
    const name = attachment.fileName;
    if (name == null || !PLAIN_FILE_NAME.test(name)) return null;
    return `/sync/v1/media/outbound/${encodeURIComponent(name)}`;
  }

  const at = ref.lastIndexOf(WORKSPACE_SEGMENT);
  if (at >= 0) {
    // Pictures only, because that is all the workspace route serves. It is
    // rooted at the agent's whole working directory — its notes, its skills,
    // the persona files openclaw writes there — so serving documents from it
    // made `MEMORY.md` readable, and the sidecar narrowed it to images.
    // Asking anyway would offer the reader a download that answers 415.
    if (attachment.type !== 'image') return null;
    const rel = ref.slice(at + WORKSPACE_SEGMENT.length);
    // No traversal, no absolute remainder, nothing empty: the same refusal
    // the sidecar makes, made before a request exists.
    if (rel.length === 0 || rel.startsWith('/')) return null;
    const segments = rel.split('/');
    if (segments.some((s) => s === '' || s === '.' || s === '..')) return null;
    return `/sync/v1/media/workspace/${segments
      .map((s) => encodeURIComponent(s))
      .join('/')}`;
  }
  return null;
}

/**
 * A URL that renders this attachment, or null while there is nothing to show.
 *
 * `previewUrl` wins when it is there: that is the copy the tab made when the
 * user picked the file, already in memory and needing no request. Otherwise
 * the agent's copy is fetched once and reused.
 *
 * A failure is silent by design. The bubble falls back to its chip, which
 * says a file is attached — the same thing it showed before this existed, and
 * a message the user cannot act on ("could not load the image") is worse than
 * the file's own label.
 */
/**
 * Whether the element is on screen, so a chat full of pictures fetches the
 * ones being looked at rather than all of them.
 *
 * Starts false and flips on the observer's first callback, which fires
 * immediately for anything already visible — so the bubble on screen loses
 * nothing, and the fifty above it cost nothing until scrolled to. A generous
 * margin means the image is usually there by the time it is.
 *
 * Where `IntersectionObserver` does not exist (jsdom, an old browser) this
 * answers true: the fetch is a nicety to defer, not a feature to lose.
 */
/**
 * The nearest ancestor that scrolls, or null for the viewport.
 *
 * Which is what an observer root has to be for a margin to mean anything
 * here: the transcript lives in its own `overflow-y: auto` container, and an
 * observer rooted at the viewport sees that container's clipping with no
 * margin applied.
 */
function scrollParent(el: Element): Element | null {
  let node: Element | null = el.parentElement;
  while (node != null) {
    const overflow = getComputedStyle(node).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

function useOnScreen(target: React.RefObject<Element | null>): boolean {
  const [seen, setSeen] = useState(
    () => typeof IntersectionObserver === 'undefined',
  );
  useEffect(() => {
    if (seen) return undefined;
    const el = target.current;
    if (el == null || typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        // Latching: once fetched, an image stays rendered whether or not it is
        // still in view. Unloading it would re-fetch on every scroll past.
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      // The SCROLLER, not the viewport. A margin expands only the root's own
      // rect, while clipping by an intermediate scroller is applied unmodified
      // — so with the implicit viewport root this margin bought nothing (the
      // transcript scrolls inside its own container) and pictures popped in on
      // arrival rather than shortly before it.
      { root: scrollParent(el), rootMargin: '600px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [seen, target]);
  return seen;
}

/**
 * Taking away a document the agent sent, or null when there is nothing to
 * take.
 *
 * A picture renders in place; a PDF or a CSV has to be saved, and it is not
 * fetched until asked for — downloading a document nobody clicked would be
 * traffic for nothing. The fetch has to happen here rather than as a link
 * because the route is authenticated and a browser sends no headers when it
 * follows an `href`; the bytes become an object URL, which a synthetic click
 * hands to the browser under the file's own name.
 *
 * Null when this is an image (it renders instead), when the message named no
 * address, or when there is no way to reach the agent — the caller then shows
 * the plain chip, and no click is offered that could not be honoured.
 */
export function useAttachmentDownload(
  attachment: MessageAttachment,
  sidecarRequest: SidecarRequest | undefined,
): { readonly start: () => void; readonly busy: boolean } | null {
  const [busy, setBusy] = useState(false);
  const route = attachment.type === 'image' ? null : mediaRouteFor(attachment);
  const name = attachment.fileName;

  const start = useCallback(() => {
    if (route == null || sidecarRequest == null || busy) return;
    setBusy(true);
    void (async () => {
      try {
        const blob = await sidecarRequest(route, { expect: 'blob' });
        if (!(blob instanceof Blob) || blob.size === 0) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = name ?? 'attachment';
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Revoked on the next tick rather than immediately: the click is
        // handled asynchronously, and releasing the bytes first gives the
        // browser an empty file. Guarded because this runs on a timer, off
        // every call stack — failing to release memory is not worth an
        // uncaught exception, and by then the download has already started.
        setTimeout(() => {
          try {
            URL.revokeObjectURL(url);
          } catch {
            /* the bytes outlive the tab at worst */
          }
        }, 0);
      } catch {
        // Silent, like the preview: the chip is still there, and a message
        // the user cannot act on is worse than letting them click again.
      } finally {
        setBusy(false);
      }
    })();
  }, [route, sidecarRequest, name, busy]);

  if (route == null || sidecarRequest == null) return null;
  return { start, busy };
}

export function useAttachmentPreview(
  attachment: MessageAttachment,
  sidecarRequest: SidecarRequest | undefined,
): {
  readonly src: string | null;
  readonly ref: React.RefObject<HTMLElement | null>;
} {
  const ref = useRef<HTMLElement | null>(null);
  const onScreen = useOnScreen(ref);
  const local = attachment.previewUrl ?? null;
  // Only a picture is fetched: a document renders as its own chip, and
  // downloading one nobody clicked would be traffic for nothing.
  const route = attachment.type === 'image' ? mediaRouteFor(attachment) : null;
  const [fetched, setFetched] = useState<string | null>(() =>
    route == null ? null : cachedPreview(route),
  );

  // Hold the cached bytes for as long as this component renders them, so
  // eviction cannot pull them out from under a mounted `<img>`.
  useEffect(() => {
    if (route == null || fetched == null) return undefined;
    retainPreview(route);
    return () => releasePreview(route);
  }, [route, fetched]);

  useEffect(() => {
    if (local != null || route == null || sidecarRequest == null) {
      return undefined;
    }
    // Nothing is fetched for a bubble nobody has scrolled to.
    if (!onScreen) return undefined;
    const known = cachedPreview(route);
    if (known != null) {
      setFetched(known);
      return undefined;
    }
    let live = true;

    let pending = inFlight.get(route);
    if (pending == null) {
      // Aborted once the LAST bubble waiting on this file has gone, so
      // scrolling quickly through a chat full of pictures does not leave a
      // dozen transfers running for bubbles nobody is looking at any more.
      // See `InFlightPreview.waiters` for why the count is needed.
      const controller = new AbortController();
      const startedAtEpoch = cacheEpoch;
      const promise = (async (): Promise<string | null> => {
        try {
          const blob = await sidecarRequest(route, {
            expect: 'blob',
            signal: controller.signal,
          });
          if (!(blob instanceof Blob) || blob.size === 0) return null;
          // WHAT THE BYTES ARE, not what the message claimed. Whether this is
          // a picture was decided entirely from the type openclaw recorded,
          // while the sidecar types its response from the file's extension —
          // so a message saying `image/jpeg` about a `.pdf` minted an object
          // URL and rendered a broken-image icon with no way back to the
          // chip, since that branch is unreachable once a src exists. It is
          // also the guard that keeps this safe if the URL ever reaches an
          // `<a href>` or an iframe, where the server's `nosniff` and
          // `Content-Disposition` would no longer be doing the work.
          if (!blob.type.startsWith('image/')) return null;
          // The account changed while this was in the air. These bytes belong
          // to the account being left, so they are dropped rather than filed —
          // checked BEFORE minting the URL, so there is nothing to revoke and
          // no chance of handing back one the clear has already revoked.
          if (startedAtEpoch !== cacheEpoch) return null;
          const url = URL.createObjectURL(blob);
          const already = cachedPreview(route);
          if (already != null) {
            URL.revokeObjectURL(url);
            return already;
          }
          remember(
            route,
            url,
            blob.size,
            route.startsWith(MUTABLE_ROUTE_PREFIX)
              ? MUTABLE_PREVIEW_TTL_MS
              : Infinity,
          );
          return url;
        } catch {
          // Silent: the chip stands in for the file.
          return null;
        } finally {
          // Only if this is still the entry for the route. A clear replaces
          // the map wholesale, and a later fetch for the same file may already
          // have filed itself here.
          if (inFlight.get(route)?.controller === controller) {
            inFlight.delete(route);
          }
        }
      })();
      pending = { promise, controller, waiters: 0 };
      inFlight.set(route, pending);
    }

    const awaited = pending;
    awaited.waiters += 1;
    void awaited.promise.then((url) => {
      if (live && url != null) setFetched(url);
    });

    return () => {
      live = false;
      awaited.waiters -= 1;
      // The last bubble waiting on this file has gone, so nothing is going to
      // render it. Guarded on the entry still being ours: a clear may have
      // dropped it, and a later fetch for the same file must not be cancelled
      // by this one's cleanup.
      if (awaited.waiters <= 0 && inFlight.get(route) === awaited) {
        awaited.controller.abort();
      }
    };
  }, [local, route, sidecarRequest, onScreen]);

  return { src: local ?? fetched, ref };
}
